
import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarTrigger,
  useSidebar,
  SidebarGroup, // Added
  // SidebarGroupContent, // Exported from SidebarGroup.tsx
  SidebarGroupLabel, // Added
} from '@/components/ui/sidebar'; 
import { CrmNavItem, CrmNavSection } from '@/types/crm';
import {
  LayoutDashboard, Briefcase, Users, Building, Calendar, BarChart2, FileText, Settings, UploadCloud, Shield, HardDrive, BriefcaseBusiness
} from 'lucide-react';

const mainNavigation: CrmNavSection[] = [
  {
    items: [
      { title: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
      { title: 'Opportunities', href: '/opportunities', icon: Briefcase },
      { title: 'Contacts', href: '/contacts2', icon: Users }, // Assuming contacts2 is the main contacts page
      { title: 'Companies', href: '/companies', icon: Building },
      { title: 'Meetings', href: '/meetings', icon: Calendar },
      { title: 'Campaigns', href: '/campaigns', icon: BarChart2 },
      { title: 'Reports', href: '/reports', icon: FileText }, // Placeholder, actual reports page might not exist
    ],
  },
  {
    title: 'DATA',
    items: [
      { title: 'Industries', href: '/industries', icon: HardDrive },
      { title: 'Suppliers', href: '/suppliers', icon: Users }, // Re-using Users icon as per screenshot simplicity
      { title: 'Projects', href: '/projects', icon: BriefcaseBusiness },
    ],
  },
  {
    title: 'TOOLS',
    items: [
      { title: 'CSV Import', href: '/csv-import', icon: UploadCloud }, // Placeholder
      { title: 'Admin', href: '/admin', icon: Shield },
      { title: 'Settings', href: '/settings', icon: Settings }, // Placeholder
    ],
  },
];

export const CrmSidebar: React.FC = () => {
  const { collapsed } = useSidebar();
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path || (path === '/dashboard' && location.pathname === '/');

  return (
    <Sidebar
      className="border-r" // Removed dynamic width classes, Sidebar component handles it
      collapsible
    >
      <SidebarHeader className={`py-4 ${collapsed ? 'justify-center px-0' : 'justify-end px-4'}`}>
        {/* This trigger is useful if the sidebar is fully collapsed without a global one */}
        {/* Or remove if TopNavbar always has a trigger */}
        <SidebarTrigger /> 
      </SidebarHeader>
      <SidebarContent>
        {mainNavigation.map((section, sectionIndex) => (
          <SidebarGroup key={section.title || `section-${sectionIndex}`}>
            {section.title && (
              <SidebarGroupLabel> {/* Use SidebarGroupLabel */}
                {section.title}
              </SidebarGroupLabel>
            )}
            <SidebarMenu>
              {section.items.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    variant={isActive(item.href) ? 'secondary' : 'ghost'}
                    className={`w-full ${isActive(item.href) ? 'bg-primary/10 text-primary hover:bg-primary/20' : 'hover:bg-muted/50'} ${collapsed ? 'justify-center' : 'justify-start'}`}
                    title={item.title} // Tooltip when collapsed
                  >
                    <NavLink to={item.href} end>
                      <item.icon className={`h-5 w-5 ${collapsed ? '' : 'mr-3'}`} />
                      {!collapsed && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroup>
        ))}
      </SidebarContent>
    </Sidebar>
  );
};
